import type { ReactNode } from 'react';
import Box from '@classify/react-core-components/Box';
import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';
import { useI18n } from '@openclassrooms/translations/I18nProvider';

import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  TwitterIcon,
  YoutubeIcon,
} from '../../../social/components';

import {
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  LINKEDIN_COLOR,
  TWITTER_COLOR,
  YOUTUBE_COLOR,
} from '../../../social/services';

function SocialButton({
  title,
  url,
  children,
  color,
}: {
  title: string;
  url: string;
  children: ReactNode;
  color: string;
}) {
  return (
    <Box
      component="a"
      href={url}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        flexShrink: 0,
        boxSizing: 'border-box',
        height: 40,
        width: 40,
        border: 1,
        borderColor: 'text.onMain',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 1,
        px: 1.25,
        transition: 'background 0.1s ease',
        '&:visited': {
          color: 'text.onMain',
        },
        '&:hover': {
          color: 'text.onContrasted',
          borderColor: 'rgb(0 0 0 / 0%)',
          background: color,
          backgroundOrigin: 'border-box',
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function FooterSocialButtons() {
  const { translate } = useI18n();
  const { getLocalisationConfig } = useLocalisationConfig();

  return (
    <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap', columnGap: '3%', rowGap: '8px' }}>
      <SocialButton
        url={String(getLocalisationConfig('social.facebook'))}
        title="Facebook"
        color={FACEBOOK_COLOR}
      >
        <FacebookIcon />
      </SocialButton>
      <SocialButton
        url={String(getLocalisationConfig('social.twitterX'))}
        title="X"
        color={TWITTER_COLOR}
      >
        <TwitterIcon title={translate('global.twitter_account')} />
      </SocialButton>
      <SocialButton
        url="https://www.linkedin.com/company/openclassrooms"
        title="LinkedIn"
        color={LINKEDIN_COLOR}
      >
        <LinkedInIcon />
      </SocialButton>
      <SocialButton
        url="https://instagram.com/openclassrooms"
        title="Instagram"
        color={INSTAGRAM_COLOR}
      >
        <InstagramIcon />
      </SocialButton>
      <SocialButton
        url={String(getLocalisationConfig('openclassrooms.youtube.url'))}
        title="YouTube"
        color={YOUTUBE_COLOR}
      >
        <YoutubeIcon />
      </SocialButton>
    </Box>
  );
}
